import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { PhoneIcon } from '@heroicons/react/24/solid';
import Logo from './Logo';
import GoogleReviews from './GoogleReviews';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const { language, translations, setLanguage } = useLanguage();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);

  // Close the language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) &&
        (mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target as Node))
      ) {
        setIsLangDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getLanguageLabel = (lang: string) => {
    switch (lang) {
      case 'de': return 'DE';
      case 'en': return 'EN';
      case 'es': return 'ES';
      case 'ru': return 'RU';
      default: return 'ES';
    }
  };

  const getLangButtonAriaLabel = () => {
    switch (language) {
      case 'de': return 'Sprache wählen';
      case 'en': return 'Choose language';
      case 'es': return 'Seleccionar idioma';
      case 'ru': return 'Выбрать язык';
      default: return 'Seleccionar idioma';
    }
  };
  
  const changeLanguage = (newLang: 'de' | 'en' | 'es' | 'ru') => {
    setLanguage(newLang);
    setIsLangDropdownOpen(false);
  };

  return (
    <header className="fixed w-full bg-white shadow-md z-50">
      {/* Social Proof / Top Bar */}
      <div className="bg-light-gray/20 border-b border-gray-100 py-1">
        <div className="container-custom flex justify-between items-center text-[11px] md:text-xs">
          <GoogleReviews />
          <div className="hidden sm:flex items-center space-x-4 text-navy/70">
            <a href="tel:+34603319648" className="hover:text-mint transition-colors flex items-center">
              <PhoneIcon className="w-3 h-3 mr-1" />
              {translations.header.phone}
            </a>
            <a href="https://wa.me/+34603319648" className="hover:text-mint transition-colors flex items-center">
              <svg className="w-3 h-3 mr-1 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              {translations.header.whatsapp}
            </a>
            <span className="hidden lg:inline opacity-60">Avenida de Jaime III, № 3, Palma</span>
          </div>
          <div className="sm:hidden flex items-center space-x-3">
            <a href="tel:+34603319648" className="text-navy/70">
              <PhoneIcon className="w-3 h-3" />
            </a>
            <a href="https://wa.me/+34603319648" className="text-navy/70">
              <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>

      <div className="container-custom py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div>
            <Link to="/">
              <Logo className="h-12 w-auto" color="#93cbb6" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-6">
            <Link to="/" className="text-navy hover:text-mint transition-colors">{translations.nav.home}</Link>
            <Link to="/implantate" className="text-navy hover:text-mint transition-colors">{translations.nav.implants}</Link>
            <Link to="/veneers" className="text-navy hover:text-mint transition-colors">{translations.nav.veneers}</Link>
            <Link to="/technologien" className="text-navy hover:text-mint transition-colors">{translations.nav.technology}</Link>
            <Link to="/kontakt" className="text-navy hover:text-mint transition-colors">{translations.nav.contact}</Link>
            <Link 
              to="/emergency" 
              className="flex items-center text-coral font-bold hover:text-coral/80 transition-colors animate-pulse"
            >
              <PhoneIcon className="w-5 h-5 mr-1" />
              <span className="relative">
                {translations.nav.emergency}
                <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-coral"></span>
              </span>
            </Link>
          </nav>

          {/* Contact & CTA */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Language Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                className="px-3 py-1 bg-navy text-white rounded-md hover:bg-coral transition-all duration-200 transform hover:scale-105 border-2 border-white font-bold flex items-center"
                aria-label={getLangButtonAriaLabel()}
                aria-expanded={isLangDropdownOpen}
              >
                {getLanguageLabel(language)}
                <svg 
                  className={`ml-1 h-4 w-4 transform transition-transform duration-200 ${isLangDropdownOpen ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isLangDropdownOpen && (
                <div className="absolute right-0 mt-2 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                  <button
                    onClick={() => changeLanguage('de')}
                    className={`block w-full text-left px-4 py-2 hover:bg-light-gray transition-colors ${language === 'de' ? 'bg-mint text-white' : 'text-navy'}`}
                    aria-label="Zu Deutsch wechseln"
                  >
                    Deutsch
                  </button>
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`block w-full text-left px-4 py-2 hover:bg-light-gray transition-colors ${language === 'en' ? 'bg-mint text-white' : 'text-navy'}`}
                    aria-label="Switch to English"
                  >
                    English
                  </button>
                  <button
                    onClick={() => changeLanguage('es')}
                    className={`block w-full text-left px-4 py-2 hover:bg-light-gray transition-colors ${language === 'es' ? 'bg-mint text-white' : 'text-navy'}`}
                    aria-label="Cambiar a Español"
                  >
                    Español
                  </button>
                  <button
                    onClick={() => changeLanguage('ru')}
                    className={`block w-full text-left px-4 py-2 hover:bg-light-gray transition-colors ${language === 'ru' ? 'bg-mint text-white' : 'text-navy'}`}
                    aria-label="Переключиться на русский"
                  >
                    Русский
                  </button>
                </div>
              )}
            </div>
            
            <Link to="/kontakt?booking=true" className="btn btn-primary">
              {translations.nav.book}
            </Link>
          </div>

          {/* Mobile Language Toggle and Menu Button */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Mobile Language Dropdown */}
            <div className="relative" ref={mobileDropdownRef}>
              <button
                onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                className="px-3 py-1 bg-navy text-white rounded-md border-2 border-white font-bold flex items-center"
                aria-label={getLangButtonAriaLabel()}
                aria-expanded={isLangDropdownOpen}
              >
                {getLanguageLabel(language)}
                <svg 
                  className={`ml-1 h-4 w-4 transform transition-transform duration-200 ${isLangDropdownOpen ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isLangDropdownOpen && (
                <div className="absolute right-0 mt-2 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                  <button
                    onClick={() => changeLanguage('de')}
                    className={`block w-full text-left px-4 py-2 hover:bg-light-gray transition-colors ${language === 'de' ? 'bg-mint text-white' : 'text-navy'}`}
                    aria-label="Zu Deutsch wechseln"
                  >
                    Deutsch
                  </button>
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`block w-full text-left px-4 py-2 hover:bg-light-gray transition-colors ${language === 'en' ? 'bg-mint text-white' : 'text-navy'}`}
                    aria-label="Switch to English"
                  >
                    English
                  </button>
                  <button
                    onClick={() => changeLanguage('es')}
                    className={`block w-full text-left px-4 py-2 hover:bg-light-gray transition-colors ${language === 'es' ? 'bg-mint text-white' : 'text-navy'}`}
                    aria-label="Cambiar a Español"
                  >
                    Español
                  </button>
                  <button
                    onClick={() => changeLanguage('ru')}
                    className={`block w-full text-left px-4 py-2 hover:bg-light-gray transition-colors ${language === 'ru' ? 'bg-mint text-white' : 'text-navy'}`}
                    aria-label="Переключиться на русский"
                  >
                    Русский
                  </button>
                </div>
              )}
            </div>
            
            <button
              className="text-navy"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="absolute top-16 left-0 right-0 bg-white shadow-xl p-4 space-y-3 z-50">
          <Link 
            to="/" 
            className="block py-2 text-navy hover:text-mint transition-colors"
            onClick={() => setIsMenuOpen(false)}
          >
            {translations.nav.home}
          </Link>
          <Link 
            to="/implantate" 
            className="block py-2 text-navy hover:text-mint transition-colors"
            onClick={() => setIsMenuOpen(false)}
          >
            {translations.nav.implants}
          </Link>
          <Link 
            to="/veneers" 
            className="block py-2 text-navy hover:text-mint transition-colors"
            onClick={() => setIsMenuOpen(false)}
          >
            {translations.nav.veneers}
          </Link>
          <Link 
            to="/technologien" 
            className="block py-2 text-navy hover:text-mint transition-colors"
            onClick={() => setIsMenuOpen(false)}
          >
            {translations.nav.technology}
          </Link>
          <Link 
            to="/kontakt" 
            className="block py-2 text-navy hover:text-mint transition-colors"
            onClick={() => setIsMenuOpen(false)}
          >
            {translations.nav.contact}
          </Link>
          <Link 
            to="/emergency" 
            className="flex items-center text-coral font-bold hover:text-coral/80 transition-colors animate-pulse"
            onClick={() => setIsMenuOpen(false)}
          >
            <PhoneIcon className="w-5 h-5 mr-1" />
            <span className="relative">
              {translations.nav.emergency}
              <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-coral"></span>
            </span>
          </Link>

          {/* Mobile Contact & Booking */}
          <div className="pt-3 border-t border-gray-200 mt-3 space-y-3">
            <Link 
              to="/kontakt?booking=true" 
              className="btn btn-primary w-full text-center block mt-4"
              onClick={() => setIsMenuOpen(false)}
            >
              {translations.nav.book}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header; 